import { useQuery } from '@tanstack/react-query';
import type { CashAccountChoice } from '@core/api/cash';
import {
  bookingPath,
  bookingPaymentAccountsPath,
  bookingsPath,
  letterheadPath,
} from '@core/api/queryPaths';
import { apiFetch } from './client';
import type { Paged } from './masters';
/**
 * Bookings, at the grain the counter actually works in (spec Section 8, with §12's group bookings
 * and §14's packages).
 *
 * <p>
 * <strong>This module exists beside `masters.ts`'s `RentalOrder`, not instead of it, for exactly as
 * long as the migration takes.</strong> The shapes here are the contract the rebuilt Section 8
 * serves; the older ones are what the current API returns. Keeping them apart means the screens
 * being rebuilt can be typed against the target while the register still compiles against what is
 * deployed, rather than both being half-right in one file.
 * </p>
 *
 * <h3>Three tracks, and why the types are shaped this way</h3>
 *
 * <p>
 * A booking carries three states that move independently, and the single status enum the first
 * implementation used had to lie about at least two of them. They are separated here:
 * </p>
 *
 * <ul>
 *   <li><strong>{@link BookingStage}</strong> on the order — what the shop and customer have
 *       agreed. Moves on decisions, never on stock.</li>
 *   <li><strong>{@link GarmentState}</strong> on each garment — where that one physical thing is.
 *       The only track that touches the availability calendar. The order's readiness is
 *       <em>derived</em> from these, which is what makes partial issue and partial return ordinary
 *       rather than each needing its own escape hatch.</li>
 *   <li><strong>Money</strong> — {@link BookingPayment} rows and a {@link BookingDeposit} that
 *       resolves on its own schedule. Never stored as a state; always the sum of what happened.</li>
 * </ul>
 *
 * <p>
 * <strong>Between the order and the garment sits a wearer</strong> ({@link BookingWearer}). One
 * person wears several things — a groom takes a sherwani, a safa and a mojari — and collects them
 * as a set, so readiness and the fitting appointment belong to a person rather than to a garment.
 * §12 asks for precisely this: "one wedding party, many people, many measurement sets, one
 * coordinated delivery date".
 * </p>
 */

/* ------------------------------------------------------------------- enums */

/**
 * These were hand-written here while the backend was being built, and are now **projected from the
 * C# enums** into `generated/enums.ts` by `AttireRent.Tests.CodeGen.TypeScriptEnums`. They are
 * re-exported rather than moved so every existing import keeps working, and so this file still
 * reads as the contract it is.
 *
 * Do not add a value here. The generated file is overwritten on every `dotnet test`, and a value
 * that disagrees with the C# enum is a runtime bug no compiler on either side catches.
 */
export {
  AmendmentKindValues,
  BookingStageValues,
  GarmentStateValues,
  PaymentKindValues,
  PaymentMethodValues,
  DepositCollectionPointValues,
  DepositRefundPointValues,
  DepositStateValues,
  GarmentPhotoPhaseValues,
} from './generated/enums';

export type {
  AmendmentKind,
  DocketLanguage,
  BookingStage,
  GarmentState,
  PaymentKind,
  PaymentMethod,
  DepositCollectionPoint,
  DepositRefundPoint,
  DepositState,
  GarmentPhotoPhase,
} from './generated/enums';

// The types live in the shared core (docs/Mobile_Apps_Design.md); re-exported so imports stay put.
export type * from '@core/api/bookings';
import type {
  Booking,
  BookingAmendment,
  BookingFilters,
  BookingPhoto,
  BookingPhotoTicket,
  BookingStageHistory,
  ConfirmBookingPhotoInput,
  LateReturnCascade,
  PresignBookingPhotoInput,
  PrintLetterhead,
  UpdateBookingInput,
} from '@core/api/bookings';
// Shared with the phone; re-exported so imports stay put.
export { bookingStageSequence } from '@core/bookings/stages';

export const bookingsKey = ['booking'] as const;

/**
 * The booking register.
 *
 * `staleTime: 0` with a refetch on focus, matching the availability search and for the same
 * reason: a register left open on a counter screen while a colleague issues garments at the next
 * till is exactly the cache that must not be trusted.
 */
export function useBookings(
  branchId: number | undefined,
  filters: BookingFilters = {},
  enabled = true,
) {
  // Every filter is in the key, spread rather than listed: a list of names left the board's `list`
  // and `dayFrom`/`dayTo` out, so two different questions shared one cached answer.
  const { page = 1, pageSize = 25 } = filters;

  return useQuery({
    queryKey: [...bookingsKey, { branchId, ...filters, page, pageSize }],
    queryFn: () => apiFetch<Paged<Booking>>(bookingsPath(filters)),
    enabled: enabled && branchId !== undefined,
    placeholderData: (previous) => previous,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/** The accounts a booking's money may go into, or a refund come out of — names and kinds only. */
export function useBookingPaymentAccounts(branchId: number | undefined) {
  return useQuery({
    queryKey: ['cash', 'paid-from', 'booking', { branchId }],
    queryFn: () => apiFetch<CashAccountChoice[]>(bookingPaymentAccountsPath()),
    enabled: branchId !== undefined,
  });
}

export function useBooking(id: number | undefined) {
  return useQuery({
    queryKey: [...bookingsKey, id],
    queryFn: () => apiFetch<Booking>(bookingPath(id ?? 0)),
    enabled: id !== undefined,
    staleTime: 0,
  });
}

export function useLetterhead(branchId: number | undefined) {
  return useQuery({
    queryKey: ['letterhead', branchId],
    queryFn: () => apiFetch<PrintLetterhead>(letterheadPath(branchId ?? 0)),
    enabled: branchId !== undefined,
    // The shop's own name and GSTIN change about once a year; re-fetching them per print is waste.
    staleTime: 60 * 60 * 1000,
  });
}

// Shared with the phone, so both send the server the same handover requests.
export {
  amendBooking,
  applyBookingDiscount,
  changeBookingStage,
  changeGarmentState,
  createBooking,
  extendBooking,
  issueBooking,
  previewIssue,
  recordBookingPayment,
  recordFitting,
  returnBooking,
  settleBookingDeposit,
} from '@core/api/bookingCommands';

/**
 * What a late return breaks downstream, and what could go instead.
 *
 * <p>
 * <strong>A read, and there is deliberately no counterpart that acts.</strong> Moving a wedding
 * booking onto a different suit is a conversation with a customer; the swap it would perform is an
 * amendment, which carries a compulsory reason for exactly this kind of reason.
 * </p>
 *
 * <p>
 * Only asked of a booking with garments still out — the server refuses the rest, so the screen only
 * enables it when something is actually out.
 * </p>
 */
export function useBookingCascade(bookingId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: [...bookingsKey, bookingId, 'cascade'],
    queryFn: () => apiFetch<LateReturnCascade>(`/api/booking/${bookingId}/cascade`),
    enabled: bookingId !== undefined && enabled,
  });
}

export function updateBooking(id: number, input: UpdateBookingInput) {
  return apiFetch<Booking>(`/api/booking/${id}`, { method: 'PUT', body: input });
}

export const bookingPhotosKey = ['booking-photo'] as const;

export function useBookingPhotos(bookingId: number | undefined) {
  return useQuery({
    queryKey: [...bookingPhotosKey, bookingId],
    queryFn: () => apiFetch<BookingPhoto[]>(`/api/booking/${bookingId}/photo`),
    enabled: bookingId !== undefined,
    // The URLs expire, so a counter left open for an hour would render broken images.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function presignBookingPhoto(
  bookingId: number,
  garmentId: number,
  input: PresignBookingPhotoInput,
) {
  return apiFetch<BookingPhotoTicket>(
    `/api/booking/${bookingId}/garment/${garmentId}/photo/presign`,
    { method: 'POST', body: input },
  );
}

export function confirmBookingPhoto(
  bookingId: number,
  garmentId: number,
  input: ConfirmBookingPhotoInput,
) {
  return apiFetch<BookingPhoto>(`/api/booking/${bookingId}/garment/${garmentId}/photo`, {
    method: 'POST',
    body: input,
  });
}

export function deleteBookingPhoto(bookingId: number, garmentId: number, photoId: number) {
  return apiFetch<void>(`/api/booking/${bookingId}/garment/${garmentId}/photo/${photoId}`, {
    method: 'DELETE',
  });
}

export function useBookingHistory(id: number | undefined) {
  return useQuery({
    queryKey: [...bookingsKey, id, 'history'],
    queryFn: () => apiFetch<BookingStageHistory[]>(`/api/booking/${id}/history`),
    enabled: id !== undefined,
  });
}

export function useBookingAmendments(id: number | undefined) {
  return useQuery({
    queryKey: [...bookingsKey, id, 'amendment'],
    queryFn: () => apiFetch<BookingAmendment[]>(`/api/booking/${id}/amendment`),
    enabled: id !== undefined,
  });
}
